import { useCallback } from 'react';

import { smallUploadByIdSelector, smallUploadUrlInfoSelector } from '@air/redux-uploader';
import { useUploadSmall } from '@air/upload-utils';

import { type PrivateUploadTaskParams } from '~/components/Upload/hooks/types';
import { usePrepareForPrivateUploading } from '~/components/Upload/privateUpload/usePrepareForPrivateUploading';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateUploadUrlsManager } from '~/providers/PrivateUploadUrlsManagerProvider';
import { useAirStore } from '~/utils/ReduxUtils';

export const useUploadSmallPrivateFile = () => {
  const store = useAirStore();
  const { uploadSmall } = useUploadSmall();
  const { prepareForUploading } = usePrepareForPrivateUploading();
  const { currentWorkspace } = useCurrentWorkspace();
  const { manager: uploadUrlsManager } = usePrivateUploadUrlsManager();
  const workspaceId = currentWorkspace?.id;

  const uploadSmallPrivateFile = useCallback(
    async ({ uploadId, onUploadFinished, onBoardCreated, onUploadStarted, onError }: PrivateUploadTaskParams) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      await uploadSmall({
        uploadId,
        onUploadStarted,
        onUploadFinished,
        onError,
        getUploadUrls: async () => {
          const upload = smallUploadByIdSelector(store.getState(), uploadId);

          if (!upload) {
            throw new Error('Upload not found');
          }
          await prepareForUploading({ upload, onBoardCreated });

          const uploadUrlInfo = smallUploadUrlInfoSelector(store.getState(), uploadId);
          const urlsInfo = uploadUrlInfo ?? (await uploadUrlsManager.getSmallUploadUrl(uploadId));

          return urlsInfo;
        },
      });
    },
    [prepareForUploading, uploadSmall, workspaceId, uploadUrlsManager.getSmallUploadUrl, store.getState],
  );

  return {
    uploadSmallPrivateFile,
  };
};
